import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Upload, Loader2, CheckCircle, FileText, MessageSquare, Send, ArrowLeft, Bot, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { ScrollArea } from '@/components/ui/scroll-area';

interface JobChatbotProps {
  jobId: string;
  jobTitle: string;
  onClose: () => void;
  initialMode?: 'chat' | 'apply';
}

// Country codes for dropdown
const countryCodes = [
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+91', country: 'India' },
  { code: '+61', country: 'Australia' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+81', country: 'Japan' },
  { code: '+86', country: 'China' },
  { code: '+65', country: 'Singapore' },
  { code: '+971', country: 'UAE' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+27', country: 'South Africa' },
  { code: '+55', country: 'Brazil' },
  { code: '+52', country: 'Mexico' },
  { code: '+31', country: 'Netherlands' },
  { code: '+34', country: 'Spain' },
  { code: '+39', country: 'Italy' },
  { code: '+46', country: 'Sweden' },
  { code: '+47', country: 'Norway' },
  { code: '+48', country: 'Poland' },
  { code: '+7', country: 'Russia' },
  { code: '+82', country: 'South Korea' },
  { code: '+60', country: 'Malaysia' },
  { code: '+62', country: 'Indonesia' },
  { code: '+63', country: 'Philippines' },
  { code: '+64', country: 'New Zealand' },
  { code: '+353', country: 'Ireland' },
  { code: '+41', country: 'Switzerland' },
  { code: '+43', country: 'Austria' },
  { code: '+32', country: 'Belgium' },
];

// Validation schema
const applicationSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  email: z.string().trim().email('Invalid email address').max(255),
  countryCode: z.string().min(1, 'Country code is required'),
  phone: z.string().trim().min(6, 'Phone number must be at least 6 digits').max(15),
});

type ApplicationForm = z.infer<typeof applicationSchema>;

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const JobChatbot = ({ jobId, jobTitle, onClose, initialMode = 'chat' }: JobChatbotProps) => {
  const [mode, setMode] = useState<'chat' | 'apply'>(initialMode);
  const [formData, setFormData] = useState<ApplicationForm>({
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+91',
    phone: '',
  });
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ApplicationForm | 'resume', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [applicationComplete, setApplicationComplete] = useState(false);
  
  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: `Hi! I'm here to help you with any questions about the ${jobTitle} position. What would you like to know?` }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('job-chatbot', {
        body: {
          message: userMessage,
          jobId,
          messages: messages.map(m => ({ role: m.role, content: m.content })),
        }
      });

      if (error) throw error;

      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
    } catch (error: any) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or click "Apply Now" to submit your application.' 
      }]);
    } finally {
      setChatLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  };

  const handleInputChange = (field: keyof ApplicationForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload a file smaller than 5MB',
          variant: 'destructive'
        });
        return;
      }

      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF or Word document',
          variant: 'destructive'
        });
        return;
      }

      setResumeFile(file);
      setFormErrors(prev => ({ ...prev, resume: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      applicationSchema.parse(formData);
      setFormErrors({});
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: Partial<Record<keyof ApplicationForm, string>> = {};
        err.issues.forEach(issue => {
          if (issue.path[0]) {
            errors[issue.path[0] as keyof ApplicationForm] = issue.message;
          }
        });
        setFormErrors(errors);
        return;
      }
    }

    if (!resumeFile) {
      setFormErrors(prev => ({ ...prev, resume: 'Please upload your resume' }));
      return;
    }

    setSubmitting(true);

    try {
      const fullPhone = `${formData.countryCode}${formData.phone}`;
      const emailLower = formData.email.trim().toLowerCase();

      let candidateId: string | null = null;
      
      const { data: existingByEmail } = await supabase
        .from('candidates')
        .select('id')
        .ilike('email_primary', emailLower)
        .maybeSingle();

      if (existingByEmail) {
        candidateId = existingByEmail.id;
      } else {
        const { data: existingByPhone } = await supabase
          .from('candidates')
          .select('id')
          .eq('phone_primary', fullPhone)
          .maybeSingle();

        if (existingByPhone) {
          candidateId = existingByPhone.id;
        }
      }

      if (!candidateId) {
        const { data: newCandidate, error: candidateError } = await supabase
          .from('candidates')
          .insert({
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            email_primary: emailLower,
            phone_primary: fullPhone,
          })
          .select('id')
          .single();

        if (candidateError) throw candidateError;
        candidateId = newCandidate.id;
      } else {
        await supabase
          .from('candidates')
          .update({
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
          })
          .eq('id', candidateId);
      }

      const sanitizedName = resumeFile.name
        .replace(/[–—]/g, '-')
        .replace(/[^\w\s.-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      
      const fileName = `${Date.now()}-${candidateId}-${sanitizedName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(fileName, resumeFile);

      if (uploadError) throw uploadError;

      const { data: resumeData, error: resumeError } = await supabase
        .from('candidate_resumes')
        .insert({
          candidate_id: candidateId,
          storage_path: fileName,
          original_filename: resumeFile.name,
          file_size: resumeFile.size,
        })
        .select('id')
        .single();

      if (resumeError) throw resumeError;

      const { data: existingApplication } = await supabase
        .from('candidate_applications')
        .select('id')
        .eq('candidate_id', candidateId)
        .eq('job_id', jobId)
        .maybeSingle();

      if (!existingApplication) {
        const { data: firstStage } = await supabase
          .from('job_stages')
          .select('id')
          .eq('job_id', jobId)
          .order('position', { ascending: true })
          .limit(1)
          .maybeSingle();

        await supabase
          .from('candidate_applications')
          .insert({
            candidate_id: candidateId,
            job_id: jobId,
            stage_id: firstStage?.id || null,
            status: 'applied',
          });
      }

      const { error: parsingError } = await supabase
        .from('parsing_jobs')
        .insert({
          candidate_id: candidateId,
          resume_id: resumeData.id,
          file_name: fileName,
          job_id: jobId,
          status: 'pending',
        });

      if (parsingError) {
        console.error('Failed to queue parsing job:', parsingError);
      }

      supabase.functions.invoke('trigger-deep-parse', {
        body: {
          candidateId,
          resumeId: resumeData.id,
          fileName,
          jobId,
        }
      }).catch(err => {
        console.error('Background parsing trigger failed:', err);
      });

      setApplicationComplete(true);
      toast({
        title: 'Application submitted!',
        description: 'Thank you for your application. We will review it shortly.',
      });

    } catch (error: any) {
      console.error('Application submission error:', error);
      toast({
        title: 'Submission failed',
        description: error.message || 'Could not submit your application. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Success state after application
  if (applicationComplete) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-xl">Application Submitted!</CardTitle>
            <CardDescription>
              Thank you for applying for <span className="font-semibold">{jobTitle}</span>. 
              Our team will review your application and get back to you soon.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md h-[600px] flex flex-col">
        <CardHeader className="relative pb-3 border-b shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
          <CardTitle className="text-lg pr-8">{jobTitle}</CardTitle>
          <CardDescription>
            {mode === 'chat' ? 'Ask questions about this position' : 'Submit your application'}
          </CardDescription>
        </CardHeader>

        {mode === 'chat' ? (
          <>
            {/* Chat Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      {msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                        <User className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="bg-muted rounded-lg px-3 py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Chat Input */}
            <div className="p-4 border-t shrink-0 space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Ask a question..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  disabled={chatLoading}
                />
                <Button size="icon" onClick={handleSendMessage} disabled={chatLoading || !chatInput.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <Button className="w-full" onClick={() => setMode('apply')}>
                Apply Now
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Application Form */}
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <ScrollArea className="flex-1 px-4">
                <div className="py-4 space-y-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setMode('chat')}
                    className="mb-2 -ml-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back to chat
                  </Button>

                  {/* Name fields */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={formErrors.firstName ? 'border-destructive' : ''}
                      />
                      {formErrors.firstName && (
                        <p className="text-xs text-destructive">{formErrors.firstName}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={formErrors.lastName ? 'border-destructive' : ''}
                      />
                      {formErrors.lastName && (
                        <p className="text-xs text-destructive">{formErrors.lastName}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john.doe@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={formErrors.email ? 'border-destructive' : ''}
                    />
                    {formErrors.email && (
                      <p className="text-xs text-destructive">{formErrors.email}</p>
                    )}
                  </div>

                  {/* Phone with country code */}
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.countryCode}
                        onValueChange={(value) => handleInputChange('countryCode', value)}
                      >
                        <SelectTrigger className="w-[120px]">
                          <SelectValue placeholder="Code" />
                        </SelectTrigger>
                        <SelectContent className="bg-background border z-[60] max-h-[200px]">
                          {countryCodes.map((cc) => (
                            <SelectItem key={cc.code} value={cc.code}>
                              {cc.code} ({cc.country})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="9876543210"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value.replace(/\D/g, ''))}
                        className={`flex-1 ${formErrors.phone ? 'border-destructive' : ''}`}
                      />
                    </div>
                    {formErrors.phone && (
                      <p className="text-xs text-destructive">{formErrors.phone}</p>
                    )}
                  </div>

                  {/* Resume upload */}
                  <div className="space-y-1.5">
                    <Label>Resume *</Label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors hover:border-primary/50 ${
                        formErrors.resume ? 'border-destructive' : 'border-muted-foreground/25'
                      } ${resumeFile ? 'bg-muted/50' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      {resumeFile ? (
                        <div className="flex items-center justify-center gap-2 text-sm">
                          <FileText className="h-5 w-5 text-primary" />
                          <span className="font-medium truncate max-w-[200px]">{resumeFile.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              setResumeFile(null);
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="py-2">
                          <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">
                            Click to upload PDF or Word document
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">Max 5MB</p>
                        </div>
                      )}
                    </div>
                    {formErrors.resume && (
                      <p className="text-xs text-destructive">{formErrors.resume}</p>
                    )}
                  </div>
                </div>
              </ScrollArea>

              <div className="p-4 border-t shrink-0">
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </Card>
    </div>
  );
};

export default JobChatbot;
